import React, { useState, useContext, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Alert,
  Chip,
  Divider,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  Grid,
  IconButton
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  CloudUpload,
  CheckCircle,
  Visibility,
  Description,
  Clear,
  Label,
  Assignment,
  QrCode,
  Download as DownloadIcon
} from '@mui/icons-material';
import { NotificationContext } from '../context/NotificationContext';
import { useProcess } from '../context/ProcessContext';
import { useActivityLog } from '../context/ActivityLogContext';
import { API_ENDPOINTS, apiCall } from '../config/api';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const AMA9999UCCLabels = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);
  const { addProcess, updateProcess, removeProcess } = useProcess();
  const {
    addActivityProcess,
    updateActivityProcess,
    addLog,
    completeActivityProcess,
  } = useActivityLog();

  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const onDrop = useCallback((acceptedFiles) => {
    const pdfFiles = acceptedFiles.filter(file =>
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );

    if (pdfFiles.length !== acceptedFiles.length) {
      showNotification('Only PDF files are supported', 'warning');
    }

    if (pdfFiles.length === 0) return;

    const newFiles = pdfFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      size: file.size,
      status: 'ready'
    }));

    setFiles(prev => [...prev, ...newFiles]);
  }, [showNotification]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true
  });

  const removeFile = (id) => {
    setFiles(files => files.filter(f => f.id !== id));
  };

  const processFiles = async () => {
    if (files.length === 0) {
      setError('Please select PDF files to process');
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');
    setResults(null);

    // Generate unique activity process ID
    const activityProcessId = `ama9999-ucc-${Date.now()}`;

    // Add to global activity log
    addActivityProcess(activityProcessId, `UCC Labels (${files.length} files)`, 'AMA9999 UCC Labels');
    addLog(activityProcessId, `Starting processing of ${files.length} PDF file(s)...`);

    const processId = addProcess({
      name: 'AMA9999 UCC Label Processing',
      status: 'processing',
      progress: 0
    });

    try {
      const formData = new FormData();
      files.forEach(fileData => {
        formData.append('pdfFiles', fileData.file);
        addLog(activityProcessId, `Adding file: ${fileData.name}`);
      });

      updateProcess(processId, {
        progress: 25,
        status: 'Uploading files...'
      });
      updateActivityProcess(activityProcessId, { progressPercentage: 25 });
      addLog(activityProcessId, 'Uploading files to server...');

      // Update file status to processing
      setFiles(prev => prev.map(f => ({ ...f, status: 'processing' })));

      const result = await apiCall(API_ENDPOINTS.AMA9999_UCC_PROCESS, {
        method: 'POST',
        body: formData
      });

      updateProcess(processId, {
        progress: 50,
        status: 'Processing PDFs...'
      });
      updateActivityProcess(activityProcessId, { progressPercentage: 50 });
      addLog(activityProcessId, 'Extracting UPC codes from PDFs...');
      addLog(activityProcessId, 'Fetching ItemIDs from AWS database...');

      // Update file status to completed
      setFiles(prev => prev.map(f => ({ ...f, status: 'completed' })));

      updateProcess(processId, {
        progress: 75,
        status: 'Downloading results...'
      });
      updateActivityProcess(activityProcessId, { progressPercentage: 75 });
      addLog(activityProcessId, 'Adding ItemIDs to labels...');

      // Download the result files
      if (result.processedFiles && result.processedFiles.length > 0) {
        let downloadedCount = 0;
        for (const processedFile of result.processedFiles) {
          if (processedFile.downloadUrl && !processedFile.error) {
            try {
              console.log('Downloading file:', processedFile.downloadUrl);
              const encodedUrl = encodeURIComponent(processedFile.downloadUrl);
              const downloadResponse = await apiCall(API_ENDPOINTS.AMA9999_UCC_DOWNLOAD(encodedUrl));

              const blob = await downloadResponse.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.style.display = 'none';
              a.href = url;
              a.download = processedFile.processedFileName || `AMA9999_UCC_Labels_${new Date().toISOString().slice(0, 19).replace(/:/g, '')}.pdf`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);

              downloadedCount++;
              addLog(activityProcessId, `Downloaded: ${processedFile.processedFileName || processedFile.originalFileName}`);

              // Small delay between downloads to prevent browser blocking
              await new Promise(resolve => setTimeout(resolve, 500));
            } catch (downloadError) {
              console.error('Error downloading file:', processedFile.downloadUrl, downloadError);
              addLog(activityProcessId, `Failed to download: ${processedFile.processedFileName}`);
              // Don't throw - continue with other files
              showNotification(`Failed to download: ${processedFile.processedFileName}`, 'error');
            }
          } else if (processedFile.error) {
            addLog(activityProcessId, `Error processing: ${processedFile.originalFileName} - ${processedFile.error}`);
          }
        }
      }

      updateProcess(processId, {
        status: 'completed',
        progress: 100
      });
      updateActivityProcess(activityProcessId, { progressPercentage: 100 });

      const successCount = result.processedFiles?.filter(f => !f.error).length || 0;
      const errorCount = result.processedFiles?.filter(f => f.error).length || 0;

      if (errorCount > 0) {
        setSuccess(`Processed ${successCount} files successfully, ${errorCount} failed`);
        addLog(activityProcessId, `Completed: ${successCount} successful, ${errorCount} failed`);
      } else {
        setSuccess(`Successfully processed ${successCount} files!`);
        addLog(activityProcessId, `Successfully processed ${successCount} file(s)`);
      }

      completeActivityProcess(activityProcessId, errorCount === 0);

      setResults({
        successCount,
        errorCount,
        processedFiles: result.processedFiles
      });

      setTimeout(() => {
        removeProcess(processId);
      }, 3000);

    } catch (error) {
      console.error('Processing error:', error);
      updateProcess(processId, {
        status: 'error',
        progress: 0
      });
      addLog(activityProcessId, `Error: ${error.message}`);
      completeActivityProcess(activityProcessId, false);
      setError(`Processing failed: ${error.message}`);
      setFiles(prev => prev.map(f => ({ ...f, status: 'error' })));

      setTimeout(() => {
        removeProcess(processId);
      }, 5000);
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setFiles([]);
    setError('');
    setSuccess('');
    setResults(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="AMA9999 UCC Labels" category="pdf" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          AMA9999 UCC LABELS
        </Typography>

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Label Processing
              </Typography>

              {/* File Upload */}
              <Card sx={{ mb: 3, bgcolor: files.length > 0 ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: TOOL_COLORS.PDF.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      Import PDF Files
                    </Typography>
                    {files.length > 0 && (
                      <Tooltip title={`${files.length} file(s) selected`}>
                        <Visibility sx={{ color: '#4CAF50' }} />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Drag & Drop Zone - Clickable */}
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: `2px dashed ${isDragActive ? TOOL_COLORS.PDF.primary : files.length > 0 ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: isDragActive
                        ? TOOL_COLORS.PDF.light
                        : files.length > 0
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      mb: 2,
                      '&:hover': {
                        borderColor: files.length > 0 ? '#4CAF50' : TOOL_COLORS.PDF.primary,
                        bgcolor: files.length > 0
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                          : TOOL_COLORS.PDF.light,
                      }
                    }}
                  >
                    <input {...getInputProps()} />
                    {files.length === 0 ? (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragActive ? TOOL_COLORS.PDF.primary : 'text.secondary', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          Drop your PDF files here or click to browse
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Supports multiple Amazon UCC label PDF files
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          {files.length} file(s) selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click or drop more files to add
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* File List */}
                  {files.length > 0 && (
                    <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                      {files.map((fileData) => (
                        <Box
                          key={fileData.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            mb: 1,
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            <Description sx={{ color: TOOL_COLORS.PDF.primary, fontSize: 20 }} />
                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {fileData.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                              ({formatFileSize(fileData.size)})
                            </Typography>
                            {fileData.status === 'processing' && (
                              <Chip label="processing" size="small" color="primary" sx={{ ml: 1 }} />
                            )}
                            {fileData.status === 'completed' && (
                              <Chip label="completed" size="small" color="success" sx={{ ml: 1 }} />
                            )}
                            {fileData.status === 'error' && (
                              <Chip label="error" size="small" color="error" sx={{ ml: 1 }} />
                            )}
                          </Box>
                          {fileData.status === 'ready' && (
                            <IconButton
                              size="small"
                              onClick={() => removeFile(fileData.id)}
                              sx={{ color: theme.palette.error.main }}
                            >
                              <Clear fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Process Button */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={processFiles}
                  disabled={files.length === 0 || processing}
                  sx={{
                    bgcolor: TOOL_COLORS.PDF.primary,
                    '&:hover': { bgcolor: TOOL_COLORS.PDF.hover },
                    px: 4
                  }}
                >
                  {processing ? 'Processing Labels...' : 'Process Labels'}
                </Button>

                {(files.length > 0 || results) && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={processing}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05)
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Status & Download Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Downloads
              </Typography>

              {/* Progress */}
              {processing && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Processing labels and adding Item IDs...
                  </Typography>
                  <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: TOOL_COLORS.PDF.primary } }} />
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Processing Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <Label sx={{ color: TOOL_COLORS.PDF.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${results.successCount} Labels Processed`}
                        secondary="Item IDs successfully added"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <QrCode sx={{ color: TOOL_COLORS.PDF.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="UPC Code Lookup"
                        secondary="ItemIDs fetched from AWS database"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Assignment sx={{ color: TOOL_COLORS.PDF.primary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${files.length} File(s)`}
                        secondary="Successfully processed and downloaded"
                      />
                    </ListItem>
                  </List>
                </Box>
              )}

              {/* Download Section - File auto-downloads after generation */}
              {results && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Labels have been automatically downloaded. Check your downloads folder.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8 }}>
                <strong>1.</strong> Upload Amazon UCC label PDF files<br />
                <strong>2.</strong> System extracts UPC codes from PDFs<br />
                <strong>3.</strong> Fetches corresponding ItemIDs from AWS<br />
                <strong>4.</strong> Adds ItemIDs to the labels<br />
                <strong>5.</strong> Download processed PDFs automatically<br /><br />
                <strong>Note:</strong> The system uses the USItems.xlsx from AWS S3 (Mappings/USItems.xlsx) for UPC to ItemID mapping.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AMA9999UCCLabels;
